import React from 'react';
import Dropzone, { DropzoneProps, DropzoneRenderArgs } from 'react-dropzone';
import { bytesToSize } from 'utils/number';

import messages from './Messages';
import Thumbnail, {
  LARGE_IMAGE_THUMBNAIL_SIZE,
} from 'components/Thumbnail/Thumbnail';
import { definedTypes } from './Messages';
import { getValidFormats, iconSelector, isImage } from 'utils/fileUtils';
import {
  Text,
  Type,
  Remove,
  InfoBox,
  Formats,
  Wrapper,
  DragAndDrop,
  UploadButton,
  StyledDropzone,
  ThumbContainer,
  FileUploadContainer,
} from './styles';
import Compressor from 'compressorjs';
import notify from '../Notify';
import { IDocumentView } from 'store/companies/companiesTypes';
import { FileTypes } from 'store/survey/surveyTypes';
import i18n from '../../i18n';

interface IFileUploadProp {
  name: string;
  file: IDocumentView | undefined;
  maxSize: number;
  required: boolean;
  onRemove: () => void;
  onAccepted: DropzoneProps['onDropAccepted'];
  onRejected: DropzoneProps['onDropRejected'];
  validFormats: FileTypes[] | null;
  allowCompressImage?: boolean;
  autoOpenEnabled?: boolean;
}

class FileUpload extends React.PureComponent<IFileUploadProp> {
  dropZoneRef: React.RefObject<Dropzone> = React.createRef<Dropzone>();

  componentDidMount() {
    if (this.props.autoOpenEnabled) {
      this.openWindow();
    }
  }

  openWindow = (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (this.dropZoneRef.current) {
      this.dropZoneRef.current.open();
    }
  };

  handleAcceptedFiles = (
    files: DropzoneRenderArgs['acceptedFiles'],
    event: any
  ) => {
    const { onAccepted, onRejected, maxSize, allowCompressImage } = this.props;
    const maxFileSize = maxSize;
    const acceptedFile = files[0];
    const { size, type } = acceptedFile;
    if (type.startsWith('image') && maxFileSize < size && allowCompressImage) {
      new Compressor(acceptedFile, {
        quality: Math.sqrt(maxFileSize / size),
        success: (result: any) => {
          if (result.size < maxFileSize) onAccepted!([result], event);
          else
            new Compressor(acceptedFile, {
              quality: (maxFileSize / size) * 0.8,
              convertSize: maxFileSize,
              success: (result: any) => {
                if (result.size < maxFileSize) onAccepted!([result], event);
                else
                  notify(
                    i18n.t('failedToCompressImage', { ns: 'survey' }) + ' 1',
                    'warning'
                  );
              },
              error: () => {
                notify(
                  i18n.t('failedToCompressImage', { ns: 'survey' }) + ' 2',
                  'warning'
                );
              },
            });
        },
        convertSize: maxFileSize,
        error: () => {
          notify(
            i18n.t('failedToCompressImage', { ns: 'survey' }) + ' 3',
            'warning'
          );
        },
      });
    } else if (size > maxFileSize) {
      onRejected!(files, event);
    } else onAccepted!([acceptedFile], event);
  };

  render() {
    const {
      file,
      maxSize,
      required,
      onRemove,
      onRejected,
      validFormats,
      allowCompressImage,
    } = this.props;

    let types: FileTypes[] = [];
    const acceptFiles: any = [];
    if (validFormats && validFormats.length > 0) {
      validFormats.forEach((type: string) =>
        acceptFiles.push(...getValidFormats[type])
      );
      types = validFormats;
    } else {
      Object.keys(definedTypes).forEach((type: string) =>
        acceptFiles.push(...getValidFormats[type])
      );
      types = Object.keys(definedTypes) as FileTypes[];
    }
    const hasImage = !!types.find((it) => it.startsWith('image'));
    const isUploadedImage = !!(file && isImage(file.properties.format));

    const thumbs =
      file && file.url ? (
        <Thumbnail
          src={
            file.thumbnailUrl ||
            (isUploadedImage && file.url) ||
            iconSelector(file.properties.format)
          }
          link={file.url}
          size={isUploadedImage ? LARGE_IMAGE_THUMBNAIL_SIZE : undefined}
        />
      ) : (
        <DragAndDrop>
          <div>{i18n.t('dragAndDrop', { ns: 'survey' }) as string}</div>
          <div>{messages.maxSizeUpload(bytesToSize(maxSize))}</div>
        </DragAndDrop>
      );

    return (
      <FileUploadContainer>
        <InfoBox>
          {file ? (
            <>
              <Text>{messages.fileName(file.properties.displayName)}</Text>
              <Text>{messages.fileSize(bytesToSize(file.fileSize))}</Text>
            </>
          ) : (
            <Formats>
              <div>{i18n.t('validFormats', { ns: 'survey' }) as string}</div>
              <Type size={10} color="blue">
                {types.map(
                  (type: FileTypes, index) =>
                    (i18n.t(`${type}`, { ns: 'survey' }) as string) +
                    (index < types.length - 1 ? ', ' : '')
                )}
              </Type>
            </Formats>
          )}
        </InfoBox>
        <Dropzone
          ref={this.dropZoneRef}
          style={StyledDropzone}
          accept={acceptFiles}
          maxSize={!(hasImage && allowCompressImage) ? maxSize : undefined}
          multiple={false}
          inputProps={{ required: required && !file }} // !file => not required when file has dropped
          disableClick={!!file}
          onDropAccepted={this.handleAcceptedFiles}
          onDropRejected={onRejected}
        >
          {/* @ts-expect-error jsx error */}
          {({ isDragActive, isDragAccept, isDragReject }) => (
            <Wrapper
              isDragAccept={isDragAccept}
              isDragActive={isDragActive}
              isDragReject={isDragReject}
              largePreview={isUploadedImage}
            >
              <ThumbContainer>
                {file && <Remove type="x" color="red" onClick={onRemove} />}
                {thumbs}
              </ThumbContainer>
            </Wrapper>
          )}
        </Dropzone>
        <UploadButton
          type="button"
          color="primary-color"
          onClick={this.openWindow}
          disabled={!!file}
        >
          {i18n.t('uploadAFile', { ns: 'survey' }) as string}
        </UploadButton>
      </FileUploadContainer>
    );
  }
}

export default FileUpload;
